/* eslint-disable no-console */
import { ActivatedRoute, type Route, Router } from '@angular/router';

import { AppKind, TellentClientBootstrapDataV2 } from '@core/tellent-types';
import {
  parametrizedRoutePath,
  tellentHomeRouteFactory,
  tellentHomeSettingsRouteFactory,
  tellentRouteFactory,
} from '@core/tellent-util';

import { TellentClient } from '../app/tellent-client';
import { PlaygroundDefaultComponent } from './playground-default/playground-default.component';

export function setupClient(
  client: TellentClient,
  data: Partial<TellentClientBootstrapDataV2> | null | void,
  appKind: `${AppKind}`,
) {
  if (!data) throw new Error('Bootstrap failed');

  console.log('onClientAuthorized', data);

  client.ready.then(appRef => {
    const router = appRef.injector.get(Router);
    const route = appRef.injector.get(ActivatedRoute);
    const defaultConfig = router.config;

    const newConfig: Route[] = [
      ...defaultConfig.slice(0, -1),
      { path: '**', component: PlaygroundDefaultComponent },
    ];
    router.resetConfig(newConfig);

    const initialUrl = router.currentNavigation()?.initialUrl;
    if (initialUrl) {
      router.navigateByUrl(initialUrl);
    } else {
      router.navigate([], { relativeTo: route });
    }
  });

  client.navigation.then(nav => {
    nav.location.set(location.pathname);
    nav.recognizedRoutes.set([
      tellentHomeRouteFactory(`${appKind}/home`),
      ...buildRecognizedRoutes(data?.features?.oneSettings!, appKind),
    ]);

    document.getElementById('open-sidebar')?.addEventListener('click', () => {
      nav.minimized.set(false);
      nav.forceMaximized.set(true);
    });

    nav.registerLinkHandler(url => {
      if (url.startsWith('http')) {
        window.open(url);
      } else {
        window.history.pushState(null, '', url);
        nav.location.set(url);
      }
    });

    window.addEventListener('popstate', () => nav.location.set(location.hash));
  });
}

const buildRecognizedRoutes = (oneSettingsEnabled: boolean, appKind: `${AppKind}`) => {
  const buildRoutes = (routes: Record<string, string>) =>
    Object.entries(routes).map(([key, value]) =>
      tellentRouteFactory(key, value.includes('/:') ? parametrizedRoutePath(value) : value),
    );

  const recognizedRoutes = [
    tellentHomeRouteFactory(`${appKind}/home`),
    ...buildRoutes(routesByAppKind[appKind].main),
  ];

  if (oneSettingsEnabled) {
    recognizedRoutes.push(
      tellentHomeSettingsRouteFactory(`${appKind}/settings/profile`),
      ...buildRoutes(routesByAppKind[appKind].settings),
    );
  }

  return recognizedRoutes;
};

const routesByAppKind: Record<
  `${AppKind}`,
  { main: Record<string, string>; settings: Record<string, string> }
> = {
  admin: {
    main: {
      home: '/admin/home',
      dashboard: '/admin/dashboard',
      profile: '/admin/profile/:userId',
      team_members: '/admin/team_members',
      company: '/admin/company',
    },
    settings: {},
  },
  recruitment: {
    main: {
      dashboard: '/recruitment/dashboard',
      profile: '/recruitment/profile/:userId',
      home: '/recruitment/home',
      candidates: '/recruitment/candidates',
      requisitions: '/recruitment/requisitions',
      jobs: '/recruitment/jobs',
      mailbox: '/recruitment/mailbox',
      analytics: '/recruitment/analytics',
      talent_pools: '/recruitment/talent_pools',
      acquisition: '/recruitment/acquisition',
      marketplace: '/recruitment/marketplace',
    },
    settings: {
      'settings:profile': '/recruitment/settings/profile',
      'settings:notifications': '/recruitment/settings/notifications',
      'settings:company_settings': '/recruitment/settings/company_settings',
      'settings:team_members': '/recruitment/settings/team_members',
      'settings:hiring_roles': '/recruitment/settings/hiring_roles',
      'settings:billing': '/recruitment/settings/billing',
      'settings:locations': '/recruitment/settings/locations',
      'settings:referrals_hub': '/recruitment/settings/referrals_hub',
      'settings:meeting_rooms': '/recruitment/settings/meeting_rooms',
      'settings:shared_mailboxes': '/recruitment/settings/shared_mailboxes',
      'settings:audit_logs': '/recruitment/settings/audit_logs',
      'settings:texting': '/recruitment/settings/texting',
      'settings:requisition_approvals': '/recruitment/settings/requisition_approvals',
      'settings:unbiased_hiring': '/recruitment/settings/unbiased_hiring',
      'settings:custom_fields': '/recruitment/settings/custom_fields',
      'settings:disqualify_reasons': '/recruitment/settings/disqualify_reasons',
      'settings:tags_and_sources': '/recruitment/settings/tags_and_sources',
      'settings:departments': '/recruitment/settings/departments',
      'settings:public_links_to_candidates': '/recruitment/settings/public_links_to_candidates',
      'settings:event_scheduler': '/recruitment/settings/event_scheduler',
      'settings:job_templates': '/recruitment/settings/job_templates',
      'settings:pipeline_templates': '/recruitment/settings/pipeline_templates',
      'settings:candidate_profile_field_templates':
        '/recruitment/settings/candidate_profile_field_templates',
      'settings:email_templates': '/recruitment/settings/email_templates',
      'settings:evaluation_form_templates': '/recruitment/settings/evaluation_form_templates',
      'settings:questionnaire_templates': '/recruitment/settings/questionnaire_templates',
      'settings:screening_question_templates': '/recruitment/settings/screening_question_templates',
      'settings:referral_question_templates': '/recruitment/settings/referral_question_templates',
      'settings:jobs_widget': '/recruitment/settings/jobs_widget',
      'settings:mobile_apps': '/recruitment/settings/mobile_apps',
      'settings:sourcing_extension': '/recruitment/settings/sourcing_extension',
      'settings:webhooks': '/recruitment/settings/webhooks',
      'settings:personal_api_tokens': '/recruitment/settings/personal_api_tokens',
      'settings:import': '/recruitment/settings/import',
    },
  },
  core_hr: {
    main: {
      dashboard: '/core_hr/dashboard',
      profile: '/core_hr/profile/:userId',
      'people:list': '/core_hr/people/list',
      'people:organization_chart': '/core_hr/people/organization_chart',
      collection: '/core_hr/collection/:collectionId',
      'journeys:my_tasks': '/core_hr/journeys/my_tasks',
      'company:details': '/core_hr/company/details',
      'ask:tickets': '/core_hr/ask-hr/tickets',
      'ask:templates': '/core_hr/ask-hr/forms',
      'ask:my_tickets': '/core_hr/ask-hr/my/tickets',
    },
    settings: {
      'settings:access_levels': '/core_hr/settings/access_levels',
      'settings:user_roles': '/core_hr/settings/user_roles',
      'settings:teams': '/core_hr/settings/teams',
      'settings:locations': '/core_hr/settings/locations',
      'settings:positions': '/core_hr/settings/positions',
      'settings:approval_flows': '/core_hr/settings/approval_flows',
      'settings:company_custom_modules': '/core_hr/settings/company_custom_modules',
      'settings:company_documents': '/core_hr/settings/company_documents',
      'settings:whistleblowing': '/core_hr/settings/whistleblowing',
      'settings:employee_custom_fields': '/core_hr/settings/employee_custom_fields',
      'settings:employee_custom_modules': '/core_hr/settings/employee_custom_modules',
      'settings:employee_documents': '/core_hr/settings/employee_documents',
      'settings:journey_types': '/core_hr/settings/journey_types',
      'settings:attendance_work_schedules': '/core_hr/settings/attendance_work_schedules',
      'settings:attendance_projects': '/core_hr/settings/attendance_projects',
      'settings:attendance_clock_in_station': '/core_hr/settings/attendance_clock_in_station',
      'settings:time_off_policies': '/core_hr/settings/time_off_policies',
      'settings:time_off_types': '/core_hr/settings/time_off_types',
      'settings:payroll_types': '/core_hr/settings/payroll_types',
      'settings:expense_categories': '/core_hr/settings/expense_categories',
      'settings:integrations_apps': '/core_hr/settings/integrations_apps',
      'settings:integrations_api_keys': '/core_hr/settings/integrations_api_keys',
    },
  },
  grow: {
    main: {
      dashboard: '/grow/dashboard',
      profile: '/grow/profile/:userId',
      overview: '/grow/overview',
      trainings: '/grow/trainings',
      goals: '/grow/goals',
      users: '/grow/users',
    },
    settings: {
      'settings:notifications': '/grow/settings/notifications',
      'settings:company': '/grow/settings/company',
      'settings:custom_fields': '/grow/settings/custom_fields',
      'settings:groups': '/grow/settings/groups',
      'settings:trainings': '/grow/settings/trainings',
    },
  },
};
